import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type GridRowId } from "@mui/x-data-grid-pro";
import { useSearchParams } from "react-router-dom";
import { SectionPaper } from "@/components/Paper";
import { userDataContext } from "@/hooks/useUserData";
import { useFeatureFlags } from "@/configs/featureFlag";
import { ExecuteModal } from "@/features/monitoring/components/ExecuteModal";
import {
  type AcquiredListView,
  type AcquiredPropertyFilters,
  type AcquiredPropertySearch,
  type AcquisitionScope,
} from "../types";
import {
  EMPTY_FILTERS,
  hasSearchFilters,
  makeSearchParams,
  readSearchState,
  writeSearchState,
} from "../utils/searchState";
import { SearchForm } from "./SearchForm";
import { FeedGrid } from "./FeedGrid";
import { AcquiredRequestFeedGrid } from "./AcquiredRequestFeedGrid";

import { useSWRConfig } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  ACQUIRED_PROPERTIES_ENDPOINT,
  getAcquiredProperties,
} from "../api/getAcquiredProperties";
import {
  ACQUIRED_REQUESTS_ENDPOINT,
  getAcquiredRequests,
} from "../api/getAcquiredRequests";
import {
  loadStoredSearchState,
  saveStoredSearchState,
} from "../utils/storedSearchState";
import { notifySearchError } from "../utils/searchError";

/** 共通検索の成功後に両タブを切り替え、失敗時は直前の結果を維持する。 */
export const AcquiredBookList: React.FC = () => {
  const { data: user } = useContext(userDataContext);
  const { realEstateRegistrationMonitoring } = useFeatureFlags();
  const [params, setParams] = useSearchParams();
  const { apiClient } = useApiClient();
  const { mutate } = useSWRConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ResultContainer = isMobile ? Box : SectionPaper;
  const urlQuery = params.toString();
  const restoredQuery = useMemo(
    () =>
      !urlQuery && user.id
        ? loadStoredSearchState(user.id, user.isSharable)
        : undefined,
    [urlQuery, user.id, user.isSharable]
  );
  const serialized = urlQuery || (restoredQuery ?? "");
  const search = useMemo(
    () => readSearchState(new URLSearchParams(serialized), user.isSharable),
    [serialized, user.isSharable]
  );
  const view: AcquiredListView =
    new URLSearchParams(serialized).get("view") === "properties"
      ? "properties"
      : "requests";
  const canonical = writeSearchState(search, view).toString();
  const [identity, setIdentity] = useState({
    id: user.id,
    isSharable: user.isSharable,
  });
  const identityChanged =
    identity.id !== user.id || identity.isSharable !== user.isSharable;
  useEffect(() => {
    if (!user.id) return;
    if (identityChanged) {
      // 初回の利用者情報取得ではURLを維持し、利用者の切替では条件も破棄する
      if (identity.id) {
        setParams(
          writeSearchState(
            {
              ...search,
              ...(identity.id !== user.id ? EMPTY_FILTERS : {}),
              scope: "self",
              page: 0,
            },
            view
          ),
          { replace: true }
        );
      }
      setIdentity({ id: user.id, isSharable: user.isSharable });
      return;
    }
    if (urlQuery !== canonical) setParams(canonical, { replace: true });
  }, [identityChanged, user.id, user.isSharable, urlQuery, canonical]);

  const [isSearching, setIsSearching] = useState(false);
  const [prefetchedKey, setPrefetchedKey] = useState<string>();
  const searchSequence = useRef(0);
  const currentView = useRef(view);
  currentView.current = view;
  const searchKey = writeSearchState(search).toString();
  const filterKey = writeSearchState({ ...search, page: 0 }).toString();
  const resultKey = JSON.stringify([user.id, user.isSharable, searchKey]);
  const revalidateIfStale = prefetchedKey !== resultKey;
  useEffect(() => {
    setIsSearching(false);
    return () => {
      searchSequence.current += 1;
    };
  }, [user.id, user.isSharable, filterKey]);

  const handleSearch = (filters: AcquiredPropertyFilters): void => {
    if (isSearching) return;
    const next = readSearchState(
      writeSearchState({ ...search, ...filters, page: 0 }),
      user.isSharable
    );
    const sequence = ++searchSequence.current;
    saveStoredSearchState(next, view, user.id, user.isSharable);
    setIsSearching(true);
    void (async () => {
      try {
        const query = makeSearchParams(next);
        // 各一覧の1ページ分だけを検索し、片方だけ新条件の結果になることを防ぐ。
        const [properties, requests] = await Promise.all([
          getAcquiredProperties(apiClient, query),
          getAcquiredRequests(apiClient, query),
        ]);
        if (searchSequence.current !== sequence) return;
        await Promise.all([
          mutate(
            [ACQUIRED_PROPERTIES_ENDPOINT, user.id, user.isSharable, query],
            properties,
            { revalidate: false }
          ),
          mutate(
            [ACQUIRED_REQUESTS_ENDPOINT, user.id, user.isSharable, query],
            requests,
            { revalidate: false }
          ),
        ]);
        if (searchSequence.current !== sequence) return;
        setPrefetchedKey(
          JSON.stringify([
            user.id,
            user.isSharable,
            writeSearchState(next).toString(),
          ])
        );
        setParams(writeSearchState(next, currentView.current));
      } catch (error) {
        if (searchSequence.current === sequence) notifySearchError(error);
      } finally {
        if (searchSequence.current === sequence) setIsSearching(false);
      }
    })();
  };

  // URL付きの直接遷移も、以降のメニューからの再訪で復元できるようにする。
  useEffect(() => {
    if (user.id && !identityChanged)
      saveStoredSearchState(search, view, user.id, user.isSharable);
  }, [user.id, user.isSharable, filterKey, identityChanged]);

  const updateSearch = (
    next: AcquiredPropertySearch,
    replace = false
  ): void => {
    saveStoredSearchState(next, view, user.id, user.isSharable);
    setParams(writeSearchState(next, view), { replace });
  };
  // ページ・タブ変更で未適用の入力をresetしないよう、フィルターの参照を維持する
  const appliedFilters = useMemo(
    () => ({
      identificationName: search.identificationName,
      prefectureCode: search.prefectureCode,
      location: search.location,
      chibanKaokuNumber: search.chibanKaokuNumber,
      acquiredDateFrom: search.acquiredDateFrom,
      acquiredDateTo: search.acquiredDateTo,
    }),
    [
      search.identificationName,
      search.prefectureCode,
      search.location,
      search.chibanKaokuNumber,
      search.acquiredDateFrom,
      search.acquiredDateTo,
    ]
  );
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<GridRowId>("");
  const [chargeCount, setChargeCount] = useState<number | null>(null);
  useEffect(() => {
    setMonitoringOpen(false);
  }, [canonical, user.id, user.isSharable]);

  if (!user.id || (identityChanged && identity.id !== user.id)) return null;

  return (
    <Stack spacing={3}>
      <SearchForm
        applied={appliedFilters}
        isLoading={isSearching}
        onSearch={handleSearch}
        onClear={() => {
          updateSearch({ ...search, ...EMPTY_FILTERS, page: 0 });
        }}
      />
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={view}
            onChange={(_event, nextView: AcquiredListView) => {
              const saved = loadStoredSearchState(user.id, user.isSharable);
              saveStoredSearchState(
                saved
                  ? readSearchState(new URLSearchParams(saved), user.isSharable)
                  : search,
                nextView,
                user.id,
                user.isSharable
              );
              setParams(writeSearchState({ ...search, page: 0 }, nextView));
            }}
            aria-label="取得済不動産登記の表示単位タブ"
          >
            <Tab label="取得依頼から探す" value="requests" />
            <Tab label="登記から探す" value="properties" />
          </Tabs>
        </Box>
        <ResultContainer>
          {user.isSharable && (
            <Stack direction="row" sx={{ p: "6px 16px" }}>
              <Select
                inputProps={{ "aria-label": "取得ユーザー" }}
                size="small"
                value={search.scope}
                onChange={(event) => {
                  updateSearch({
                    ...search,
                    scope: event.target.value as AcquisitionScope,
                    page: 0,
                  });
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">全て</MenuItem>
                <MenuItem value="self">自分で取得</MenuItem>
                <MenuItem value="other">他ユーザー取得</MenuItem>
              </Select>
            </Stack>
          )}
          {!identityChanged &&
            (view === "requests" ? (
              <AcquiredRequestFeedGrid
                search={search}
                userId={user.id}
                isSharable={user.isSharable}
                revalidateIfStale={revalidateIfStale}
                onSearchChange={updateSearch}
                setSelectedId={setSelectedId}
                setOpenMonitoringExecuteConfirmDialog={setMonitoringOpen}
                setChargeCountClickedMonitoringExecuteButton={setChargeCount}
              />
            ) : !hasSearchFilters(search) ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography color="text.secondary">
                  上の検索条件を指定して検索してください
                </Typography>
              </Box>
            ) : (
              <FeedGrid
                search={search}
                userId={user.id}
                isSharable={user.isSharable}
                revalidateIfStale={revalidateIfStale}
                onSearchChange={updateSearch}
              />
            ))}
        </ResultContainer>
      </Box>
      {/* モーダル内に独立して保持される課金確認も、一覧状態の変更で破棄する */}
      {view === "requests" &&
        realEstateRegistrationMonitoring &&
        monitoringOpen &&
        chargeCount !== null &&
        chargeCount > 0 && (
          <ExecuteModal
            open={monitoringOpen}
            setOpen={setMonitoringOpen}
            chargeCount={chargeCount}
            pictureRequestId={Number(selectedId)}
          />
        )}
    </Stack>
  );
};
